import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Menu, UserRound, X } from 'lucide-react';
import { useAuth, useIsPlatformAdmin } from '@/providers/AuthProvider';
import { useTenant } from '@/providers/TenantProvider';
import { useShell } from '@/providers/ShellProvider';
import { CommandPaletteTrigger } from '@/shared/components/CommandPalette';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { NotificationBell } from './NotificationBell';
import { UI_LABELS } from '@/config/ui-labels';

function formatRoleLabel(role: string | undefined) {
  if (!role) return '';
  if (role === 'TENANT_ADMIN') return 'Organization admin';
  if (role === 'VOICERA_ADMIN') return 'Platform admin';
  if (role === 'USER') return 'Agent';
  return role.replace(/_/g, ' ');
}

/** Global header — mobile menu, search, organization context, notifications, user menu. */
export function GlobalHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, logout } = useAuth();
  const { tenantName, tenantNumber, hasTenant, clearActiveTenant } = useTenant();
  const isPlatformAdmin = useIsPlatformAdmin();
  const { toggleMobile } = useShell();

  const onPlatformRoute = location.pathname.startsWith('/platform');
  const showTenantExit = isPlatformAdmin && hasTenant && !onPlatformRoute;

  function exitTenant() {
    clearActiveTenant();
    navigate('/platform/tenants');
  }

  return (
    <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border bg-card/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-card/80 md:gap-4 md:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-4">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 md:hidden"
          aria-label="Open navigation"
          onClick={toggleMobile}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <CommandPaletteTrigger />
      </div>
      <div className="flex items-center gap-2 md:gap-3">
        {showTenantExit ? (
          <div className="hidden items-center gap-2 rounded-md border border-primary/25 bg-primary/5 px-2 py-1 text-sm sm:flex">
            <span className="max-w-[8rem] truncate font-medium text-primary md:max-w-[12rem]">
              {tenantName}
            </span>
            {tenantNumber > 0 && (
              <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-xs text-primary">
                #{tenantNumber}
              </span>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 gap-1 px-2 text-xs text-primary hover:bg-primary/10"
              onClick={exitTenant}
            >
              <X className="h-3.5 w-3.5" />
              Exit {UI_LABELS.tenant.toLowerCase()}
            </Button>
          </div>
        ) : (
          <div className="hidden items-center gap-2 text-sm sm:flex">
            <span className="max-w-[10rem] truncate font-medium md:max-w-none">
              {hasTenant && !isPlatformAdmin ? tenantName : 'Platform'}
            </span>
            {hasTenant && !isPlatformAdmin && tenantNumber > 0 && (
              <span className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                #{tenantNumber}
              </span>
            )}
          </div>
        )}
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                <UserRound className="h-4 w-4" />
              </span>
              <span className="hidden max-w-[8rem] truncate md:inline">
                {session?.user.display_name}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>{session?.user.display_name}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {session?.user.email}
                </span>
                <span className="text-xs font-normal text-muted-foreground">
                  {formatRoleLabel(session?.user.role)}
                  {hasTenant && tenantNumber > 0
                    ? ` · ${UI_LABELS.organizationNumber}${tenantNumber}`
                    : ''}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings/account" className="flex items-center gap-2">
                <UserRound className="h-4 w-4 text-primary" />
                {UI_LABELS.myAccount}
              </Link>
            </DropdownMenuItem>
            {showTenantExit && (
              <DropdownMenuItem onClick={exitTenant}>
                Exit {UI_LABELS.tenant.toLowerCase()}
              </DropdownMenuItem>
            )}
            {isPlatformAdmin && (
              <DropdownMenuItem asChild>
                <Link to="/platform/dashboard" onClick={() => clearActiveTenant()}>
                  Platform console
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => logout()}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
